import { HttpClient, HttpContext, HttpContextToken, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, of, switchMap, EMPTY, tap, catchError, throwError } from 'rxjs';
import { filter, skip, take } from 'rxjs/operators';
import { ModalService } from '@app/shared/services/modal/modal.service';
import { NotificationService } from '@app/core/services/notification.service';
import { OverlaySpinnerService } from '@app/shared/components/overlay-spinner/services/overlay-spinner.service';
import { MODEL_CONFIG } from '@app/core/config/model.config';
import { ACTION_CONFIG } from '@app/core/config/action.config';
import { API_URLS } from '@app/core/config/url.config';

const SKIP_ASSIGNMENT_CHECK = new HttpContextToken<boolean>(() => false);

type AxelorDataItem = { info?: { message?: string }; error?: { message?: string } };
type AxelorFlatData = { message?: string };

interface AxelorResponse {
  status: number;
  data?: AxelorDataItem[] | AxelorFlatData;
}

export const assignmentErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);

  // Only active on delivery-card routes (opening + finishing)
  if (!router.url.startsWith('/delivery-card-')) {
    return next(req);
  }

  // Skip re-interception for the assignment API call (context token, not a header)
  if (req.context.get(SKIP_ASSIGNMENT_CHECK)) {
    return next(req);
  }

  const modalService = inject(ModalService);
  const http = inject(HttpClient);
  const notificationService = inject(NotificationService);
  const overlaySpinnerService = inject(OverlaySpinnerService);

  return next(req).pipe(
    switchMap(event => {
      if (!(event instanceof HttpResponse)) return of(event);

      const body = event.body as AxelorResponse;
      if (!isAssignmentError(body)) return of(event);

      const currentUrl = router.url;
      const cardIdMatch = currentUrl.match(/\/edit\/(\d+)/);
      const cardId = cardIdMatch ? cardIdMatch[1] : null;

      if (!cardId) {
        console.error('Could not extract delivery card ID from URL:', currentUrl);
        notificationService.notify('Ошибка: не удалось определить ID карточки доставки', 'danger');
        return of(event);
      }

      const errorMessage: string = extractMessage(body);

      return openAssignmentModal(modalService, overlaySpinnerService, errorMessage).pipe(
        switchMap(confirmed => {
          if (!confirmed) return EMPTY;

          const assignPayload = {
            model: MODEL_CONFIG.DELIVERY_CARDS.MODEL,
            action: ACTION_CONFIG.DELIVERY_CARD_ASSIGN_INSPECTOR,
            data: {
              criteria: [],
              context: {
                _model: MODEL_CONFIG.DELIVERY_CARDS.MODEL,
                id: parseInt(cardId, 10)
              }
            }
          };

          // Use HttpContext instead of a custom header — context is never sent to the server
          return http.post<AxelorResponse>(API_URLS.BASE_URL_ACTION, assignPayload, {
            context: new HttpContext().set(SKIP_ASSIGNMENT_CHECK, true),
            withCredentials: true
          }).pipe(
            switchMap((assignResponse) => {
              overlaySpinnerService.hide();

              // Check if assignment itself failed with assignment error
              if (isAssignmentError(assignResponse)) {
                const assignErrorMsg = extractMessage(assignResponse) || 'Не удалось назначить карточку';
                notificationService.notify(assignErrorMsg, 'danger');
                return EMPTY;
              }

              // Assignment succeeded — show success message if available
              const successMessage = extractMessage(assignResponse);
              if (successMessage) {
                notificationService.notify(successMessage, 'success');
              }


              // Axelor GET /ws/rest/:model/:id returns data as an array: { data: [{ version, ... }] }
              return http.get<{ status: number; data?: Array<{ version?: number }> | { version?: number } }>(
                `${API_URLS.BASE_URL_REST}/${MODEL_CONFIG.DELIVERY_CARDS.MODEL}/${cardId}`,
                { context: new HttpContext().set(SKIP_ASSIGNMENT_CHECK, true), withCredentials: true }
              ).pipe(
                switchMap((record) => {
                  const rawData = record?.data;
                  const newVersion = Array.isArray(rawData) ? rawData[0]?.version : rawData?.version;
                  let retryReq = req;

                  if (newVersion !== undefined) {
                    const originalBody = req.body as Record<string, unknown> | null;
                    const dataObj = originalBody?.['data'] as Record<string, unknown> | undefined;
                    if (dataObj && 'version' in dataObj) {
                      retryReq = req.clone({ body: { ...originalBody, data: { ...dataObj, version: newVersion } } });
                    }
                  }

                  return next(retryReq).pipe(
                    tap(() => overlaySpinnerService.hide()),
                    catchError((retryError) => {
                      overlaySpinnerService.hide();
                      console.error('Retry of original request failed:', retryError);
                      return throwError(() => retryError);
                    })
                  );
                }),
                catchError(() =>
                  // Version fetch failed — fall back to original request as-is
                  next(req).pipe(
                    tap(() => overlaySpinnerService.hide()),
                    catchError((retryError) => {
                      overlaySpinnerService.hide();
                      return throwError(() => retryError);
                    })
                  )
                )
              );
            }),
            catchError((error) => {
              overlaySpinnerService.hide();
              notificationService.notify('Ошибка при назначении карточки', 'danger');
              console.error('Assignment request failed:', error);
              return throwError(() => error);
            })
          );
        })
      );
    })
  );
};

function extractMessage(body: AxelorResponse): string {
  if (!body?.data) return '';
  if (!Array.isArray(body.data)) return body.data.message || '';
  return body.data[0]?.info?.message || body.data[0]?.error?.message || '';
}

function isAssignmentError(body: AxelorResponse): boolean {
  const ASSIGNMENT_ERROR_TEXT = 'Над данной карточкой доставки работает другой инспектор';
  return extractMessage(body).includes(ASSIGNMENT_ERROR_TEXT);
}

function openAssignmentModal(
  modalService: ModalService,
  overlaySpinnerService: OverlaySpinnerService,
  errorMessage: string
): Observable<boolean> {
  const result$ = new Subject<boolean>();
  let settled = false;

  const settle = (value: boolean) => {
    if (!settled) {
      settled = true;
      result$.next(value);
      result$.complete();
    }
  };

  overlaySpinnerService.hide();

  modalService.modalData$.pipe(
    skip(1),
    filter(d => d === null),
    take(1)
  ).subscribe(() => settle(false));

  modalService.openModal({
    title: 'Требуется назначение',
    body: errorMessage,
    confirmText: 'Назначить на себя',
    cancelText: 'Отмена',
    onConfirm: () => {
      settle(true);
      overlaySpinnerService.show({ message: 'Назначение карточки...' });
      modalService.closeModal();
    },
    contentTemplate: null
  });

  return result$;
}
